import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { WEEK, DAY_LABELS } from "@/lib/hh";
import { Field, StateSelect } from "@/components/FormBits";

const emptyHours = () => Object.fromEntries(WEEK.map((d) => [d, { on: false, start: "16:00", end: "18:00" }]));

function HoursEditor({ hours, setHours }) {
  const upd = (d, k, v) => setHours((h) => ({ ...h, [d]: { ...h[d], [k]: v } }));
  return (
    <div className="space-y-2">
      {WEEK.map((d) => (
        <div key={d} className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-800 p-3">
          <Switch data-testid={`hours-toggle-${d}`} checked={hours[d].on} onCheckedChange={(v) => upd(d, "on", v)} />
          <span className="w-24 text-sm font-semibold text-zinc-200">{DAY_LABELS[d]}</span>
          {hours[d].on ? (
            <div className="flex items-center gap-2">
              <Input type="time" data-testid={`hours-start-${d}`} value={hours[d].start} onChange={(e) => upd(d, "start", e.target.value)} className="h-9 w-32 bg-zinc-950" />
              <span className="text-zinc-500">to</span>
              <Input type="time" data-testid={`hours-end-${d}`} value={hours[d].end} onChange={(e) => upd(d, "end", e.target.value)} className="h-9 w-32 bg-zinc-950" />
            </div>
          ) : <span className="text-sm text-zinc-600">No happy hour</span>}
        </div>
      ))}
    </div>
  );
}

function SpecialsEditor({ specials, setSpecials }) {
  const upd = (i, k, v) => setSpecials((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2">
      {specials.map((s, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <select data-testid={`special-category-${i}`} value={s.category} onChange={(e) => upd(i, "category", e.target.value)} className="h-10 rounded-md border border-input bg-zinc-950 px-2 text-sm">
            <option value="drink">Drink</option>
            <option value="food">Food</option>
          </select>
          <Input data-testid={`special-item-${i}`} placeholder="e.g. House margarita" value={s.item} onChange={(e) => upd(i, "item", e.target.value)} className="h-10 min-w-[160px] flex-1 bg-zinc-950" />
          <Input data-testid={`special-price-${i}`} type="number" min="0" step="0.01" placeholder="$" value={s.price} onChange={(e) => upd(i, "price", e.target.value)} className="h-10 w-24 bg-zinc-950" />
          <button type="button" data-testid={`special-remove-${i}`} onClick={() => setSpecials((x) => x.filter((_, j) => j !== i))} disabled={specials.length === 1} className="rounded-lg p-2 text-zinc-500 hover:text-rose-400 disabled:opacity-30" aria-label="Remove special"><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" data-testid="special-add" onClick={() => setSpecials((s) => [...s, { category: "drink", item: "", price: "" }])} className="chip"><Plus size={14} /> Add special</button>
    </div>
  );
}

export default function SubmitSpecials() {
  const [f, setF] = useState({ bar_name: "", address: "", city: "", state: "", phone: "", website: "", owner_name: "", email: "" });
  const [hours, setHours] = useState(emptyHours);
  const [specials, setSpecials] = useState([{ category: "drink", item: "", price: "" }]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.state) return toast.error("Please pick your state");
    const h = Object.fromEntries(WEEK.map((d) => [d, hours[d].on ? { start: hours[d].start, end: hours[d].end } : null]));
    if (!Object.values(h).some(Boolean)) return toast.error("Turn on at least one happy hour day");
    const sp = specials.filter((s) => s.item.trim() && s.price !== "").map((s) => ({ ...s, item: s.item.trim(), price: Number(s.price) }));
    if (!sp.length) return toast.error("Add at least one special with a price");
    setBusy(true);
    try {
      const { data } = await api.post("/submissions", { ...f, hours: h, specials: sp });
      setDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="page max-w-2xl">
        <div data-testid="submit-success" className="panel p-8">
          <CheckCircle2 className="mb-3 text-emerald-400" size={32} />
          <h1 className="font-heading text-4xl text-white">Thanks! Your specials are in review.</h1>
          <p className="mt-3 text-zinc-300">Our team will approve your listing shortly. Once approved, you can boost it to the top of results with a Featured listing.</p>
          {!done.located && <p className="mt-3 text-sm text-amber-300">We couldn't pin your address on the map automatically. The admin will check it.</p>}
          <Link to="/featured" className="mt-5 inline-block font-semibold text-[#fde68a] hover:underline">About Featured listings →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page max-w-3xl">
      <p className="eyebrow">For bar & restaurant owners</p>
      <h1 className="font-heading mt-2 text-5xl text-white sm:text-6xl">Own a Bar? Submit Your Specials</h1>
      <p className="mt-3 text-zinc-400">Free to list. Submissions are reviewed by our team before they appear.</p>
      <form onSubmit={submit} className="mt-8 space-y-8">
        <section className="panel grid gap-4 p-6 sm:grid-cols-2">
          <Field label="Bar name" id="submit-bar-name" required value={f.bar_name} onChange={set("bar_name")} />
          <Field label="Phone" id="submit-phone" required type="tel" value={f.phone} onChange={set("phone")} />
          <div className="sm:col-span-2"><Field label="Street address" id="submit-address" required value={f.address} onChange={set("address")} /></div>
          <Field label="City" id="submit-city" required value={f.city} onChange={set("city")} />
          <StateSelect value={f.state} onChange={(v) => setF({ ...f, state: v })} testId="submit-state-select" />
          <div className="sm:col-span-2"><Field label="Website (optional)" id="submit-website" value={f.website} onChange={set("website")} /></div>
          <Field label="Owner name" id="submit-owner-name" required value={f.owner_name} onChange={set("owner_name")} />
          <Field label="Email" id="submit-email" required type="email" value={f.email} onChange={set("email")} />
        </section>
        <section className="panel p-6">
          <p className="eyebrow mb-4">Happy hour days & times</p>
          <HoursEditor hours={hours} setHours={setHours} />
        </section>
        <section className="panel p-6">
          <p className="eyebrow mb-4">Specials with prices</p>
          <SpecialsEditor specials={specials} setSpecials={setSpecials} />
        </section>
        <button data-testid="submit-specials-button" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#fde68a] font-bold text-zinc-950 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Submit for approval
        </button>
      </form>
    </div>
  );
}
